import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { DatePicker } from "../DatePicker";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";

const STATUSES = { scheduled: "Scheduled", in_progress: "In progress", completed: "Completed", cancelled: "Cancelled" };
const sel = "h-9 w-full rounded-md border border-input bg-white px-2 text-sm";
const L = ({ children }) => <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-zinc-500">{children}</Label>;

// Create a job from an approved quote (quoteId) or edit an existing job (job)
export const JobDialog = ({ open, onOpenChange, job, quoteId, onSaved }) => {
  const [f, setF] = useState({});
  const [installers, setInstallers] = useState([]);
  useEffect(() => {
    if (!open) return;
    api.get("/installers").then((r) => setInstallers(r.data));
    setF(job ? { ...job } : { scheduled_date: "", scheduled_time: "", installer_id: "", notes: "" });
  }, [open, job]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.scheduled_date) return toast.error("Please choose an install date");
    try {
      const body = { scheduled_date: f.scheduled_date, scheduled_time: f.scheduled_time, installer_id: f.installer_id, notes: f.notes };
      const r = job ? await api.put(`/jobs/${job.id}`, { ...body, status: f.status }) : await api.post("/jobs", { ...body, quote_id: quoteId });
      toast.success(job ? "Job updated" : `Job ${r.data.number} created`);
      onSaved?.(r.data);
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete job ${job.number}?`)) return;
    try { await api.delete(`/jobs/${job.id}`); toast.success("Job deleted"); onSaved?.(null); onOpenChange(false); }
    catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="job-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">{job ? `Job ${job.number}` : "Create job"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><L>Install date *</L><DatePicker testId="job-date-input" value={f.scheduled_date} onChange={(v) => set("scheduled_date", v)} /></div>
            <div><L>Start time</L><Input data-testid="job-time-input" type="time" value={f.scheduled_time || ""} onChange={(e) => set("scheduled_time", e.target.value)} /></div>
          </div>
          <div><L>Assigned installer</L>
            <select data-testid="job-installer-select" className={sel} value={f.installer_id || ""} onChange={(e) => set("installer_id", e.target.value)}>
              <option value="">Unassigned</option>
              {installers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          {job && <div><L>Status</L>
            <select data-testid="job-status-select" className={sel} value={f.status} onChange={(e) => set("status", e.target.value)}>
              {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>}
          <div><L>Job notes</L><Textarea data-testid="job-notes-input" rows={3} value={f.notes || ""} placeholder="Access, parking, site contact…" onChange={(e) => set("notes", e.target.value)} /></div>
          <DialogFooter className="gap-2 sm:justify-between">
            {job ? <Button data-testid="job-delete-button" type="button" variant="ghost" className="text-red-600" onClick={remove}><Trash2 className="mr-1 h-4 w-4" />Delete job</Button> : <span />}
            <Button data-testid="job-save-button" type="submit" className="bg-red-600 hover:bg-red-700">{job ? "Save job" : "Create job"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
